// =============================================================================
// schoolDiscovery.ts — data loading + client-side filtering (v0.2.32)
// =============================================================================
//
// The discovery dataset (~6,300 schools) ships as a static JSON asset at
// /data/schools-dataset.json. It is fetched once at runtime and cached in
// module scope; all filtering happens client-side in plain JavaScript.
//
// Honest "unknown" handling: a school missing a field a filter uses is NOT
// silently dropped. Numeric-range filters have an `includeUnknown` flag
// (default true) so a school with, say, no admit rate still appears unless the
// user explicitly narrows it out. The UI surfaces "not reported" for nulls.
// =============================================================================

import type { DiscoverySchool, DiscoveryDataset } from './schoolDiscoveryTypes';
import { cipFamily, localeBucket } from './schoolDiscoveryTypes';

let cache: DiscoveryDataset | null = null;
let inflight: Promise<DiscoveryDataset> | null = null;

/** Fetch (once) and cache the bundled discovery dataset. */
export async function loadDiscoveryDataset(): Promise<DiscoveryDataset> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = fetch('/data/schools-dataset.json')
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to load school data (HTTP ${res.status})`);
      return res.json();
    })
    .then((data: DiscoveryDataset) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((e) => {
      inflight = null;
      throw e;
    });
  return inflight;
}

/** A single school from the cached dataset, by id. */
export function getSchoolById(id: number): DiscoverySchool | null {
  if (!cache) return null;
  return cache.schools.find((s) => s.id === id) ?? null;
}

// --- Filter model -----------------------------------------------------------

export interface NumericRange {
  min: number | null;
  max: number | null;
  includeUnknown: boolean;
}

export interface DiscoveryFilters {
  text: string;                       // name/city search
  schoolTypes: number[];              // degreePredominant values; [] = all
  states: string[];                   // state codes; [] = all
  settings: string[];                 // City/Suburb/Town/Rural; [] = all
  ownership: number[];                // [] = all
  religiousOnly: boolean;             // only schools with a religious affiliation
  hsiOnly: boolean;
  majorFamily: string | null;         // 2-digit CIP family
  majorCode: string | null;           // specific 4-digit CIP
  admitRate: NumericRange;            // 0..1
  satAvg: NumericRange;
  netPrice: NumericRange;
  size: NumericRange;
}

/** A blank filter set — the discovery tab's starting point.
 *  Per the design, school type defaults to four-year (degreePredominant 3). */
export function defaultFilters(): DiscoveryFilters {
  const range = (): NumericRange => ({ min: null, max: null, includeUnknown: true });
  return {
    text: '',
    schoolTypes: [3],
    states: [],
    settings: [],
    ownership: [],
    religiousOnly: false,
    hsiOnly: false,
    majorFamily: null,
    majorCode: null,
    admitRate: range(),
    satAvg: range(),
    netPrice: range(),
    size: range(),
  };
}

function inRange(value: number | null, range: NumericRange): boolean {
  if (value == null) return range.includeUnknown;
  if (range.min != null && value < range.min) return false;
  if (range.max != null && value > range.max) return false;
  return true;
}

/** Apply a filter set to the dataset. Pure function over the cached schools. */
export function filterSchools(
  schools: DiscoverySchool[],
  f: DiscoveryFilters
): DiscoverySchool[] {
  const text = f.text.trim().toLowerCase();

  return schools.filter((s) => {
    if (text) {
      const hay = `${s.name} ${s.city ?? ''} ${s.state ?? ''}`.toLowerCase();
      if (!hay.includes(text)) return false;
    }
    if (f.schoolTypes.length && (s.degreePredominant == null ||
        !f.schoolTypes.includes(s.degreePredominant))) return false;
    if (f.states.length && (s.state == null || !f.states.includes(s.state))) return false;
    if (f.settings.length) {
      const bucket = localeBucket(s.locale);
      if (bucket == null || !f.settings.includes(bucket)) return false;
    }
    if (f.ownership.length && (s.ownership == null ||
        !f.ownership.includes(s.ownership))) return false;
    if (f.religiousOnly && (s.religious == null)) return false;
    if (f.hsiOnly && s.hsi !== 1) return false;

    // Major filter — a specific 4-digit code takes precedence over family.
    if (f.majorCode) {
      if (!s.majors.some((m) => m.code === f.majorCode)) return false;
    } else if (f.majorFamily) {
      if (!s.majors.some((m) => cipFamily(m.code) === f.majorFamily)) return false;
    }

    if (!inRange(s.admitRate, f.admitRate)) return false;
    if (!inRange(s.satAvg, f.satAvg)) return false;
    if (!inRange(s.netPrice, f.netPrice)) return false;
    if (!inRange(s.size, f.size)) return false;

    return true;
  });
}

/** Distinct state codes present in the dataset, sorted. */
export function statesInDataset(schools: DiscoverySchool[]): string[] {
  const set = new Set<string>();
  for (const s of schools) if (s.state) set.add(s.state);
  return [...set].sort();
}

/** True if any filter is set to something other than its default. */
export function filtersAreActive(f: DiscoveryFilters): boolean {
  const d = defaultFilters();
  return JSON.stringify(f) !== JSON.stringify(d);
}
